import React, { useState, useEffect } from 'react';
import {
  Sparkles, Upload, FileText, Cpu, Zap, AlertTriangle, Info, Loader2, Check,
  ArrowRight, Download, ShieldCheck, Activity, ChevronRight, Wand2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_BIN_BYTES = 16 * 1024 * 1024;
const MAX_LOG_BYTES = 8 * 1024 * 1024;

/**
 * Peek at the first ~256 KB of a datalog and classify it as 'idle' vs 'wot'.
 * Heuristic: an idle log has almost no rows above 2000 RPM and pedal < 5%;
 * a WOT log has a meaningful sample of rows above 4500 RPM with pedal > 70%.
 * Returns { mode, reason, samples } or null if we can't parse a header.
 */
async function detectLogMode(file) {
  try {
    const blob = file.slice(0, Math.min(file.size, 262144)); // 256 KB peek
    const text = await blob.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 3) return null;

    // Find header row — typically first non-empty line with commas + "RPM"
    let headerIdx = 0;
    for (let i = 0; i < Math.min(lines.length, 6); i++) {
      if (/rpm/i.test(lines[i]) && lines[i].includes(',')) { headerIdx = i; break; }
    }
    const headers = lines[headerIdx].split(',').map(h => h.trim().toLowerCase());
    const rpmCol = headers.findIndex(h => /rpm|engine\s*speed/.test(h));
    const pedalCol = headers.findIndex(h => /pedal|throttle|tps|accel/.test(h));
    if (rpmCol < 0) return null;

    let highRpmHighPedal = 0;
    let lowRpmLowPedal = 0;
    let total = 0;
    for (let i = headerIdx + 1; i < lines.length; i++) {
      const cells = lines[i].split(',');
      if (cells.length <= rpmCol) continue;
      const rpm = parseFloat(cells[rpmCol]);
      const pedal = pedalCol >= 0 ? parseFloat(cells[pedalCol]) : NaN;
      if (!isFinite(rpm)) continue;
      total++;
      if (rpm >= 4500 && (!isFinite(pedal) || pedal >= 70)) highRpmHighPedal++;
      if (rpm > 500 && rpm < 1500 && (!isFinite(pedal) || pedal < 5)) lowRpmLowPedal++;
    }
    if (total < 10) return null;

    // WOT log: at least ~30 rows above 4500 RPM at high pedal
    if (highRpmHighPedal >= 30) {
      return { mode: 'wot', reason: `${highRpmHighPedal} rows above 4500 RPM detected`, samples: total };
    }
    // Idle log: >60% of rows sit in the warm-idle band
    if (lowRpmLowPedal / total > 0.6) {
      return { mode: 'idle', reason: `${lowRpmLowPedal}/${total} rows in idle band (500-1500 RPM)`, samples: total };
    }
    // Mixed / cruise / unclear — leave mode alone
    return { mode: null, reason: 'mixed driving — pick mode manually', samples: total };
  } catch (_e) {
    return null;
  }
}

function DropZone({ label, sublabel, accept, maxLabel, icon: Icon, file, onFile, error, testId, accent }) {
  const [drag, setDrag] = useState(false);
  const handleFile = (f) => { if (f) onFile(f); };

  return (
    <label
      className={`block border-2 border-dashed rounded-sm p-5 cursor-pointer transition-all ${
        error
          ? 'border-red-500/50 bg-red-500/5'
          : file
            ? 'border-emerald-500/50 bg-emerald-500/5'
            : drag
              ? `border-${accent} bg-${accent}/10 scale-[1.01]`
              : 'border-zinc-700 hover:border-[#FF5E1A]/60 hover:bg-[#FF5E1A]/[0.03] bg-[#0A0A0A]'
      }`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
      data-testid={testId}
    >
      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      {file ? (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
            <Check className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-['JetBrains_Mono'] truncate" data-testid={`${testId}-name`}>{file.name}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {(file.size / 1024).toFixed(1)} KB · click or drop to change
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-sm border flex items-center justify-center flex-shrink-0 border-${accent}/40 bg-${accent}/10`}>
            <Icon className={`w-5 h-5 text-${accent}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-semibold">{label}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{sublabel} · {maxLabel}</p>
          </div>
          <Upload className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        </div>
      )}
    </label>
  );
}

export default function AITuneIterator() {
  const [currentBin, setCurrentBin] = useState(null);
  const [datalog, setDatalog] = useState(null);
  const [fuel, setFuel] = useState('E85');
  const [mode, setMode] = useState('wot');
  const [detectedMode, setDetectedMode] = useState(null); // { mode, reason, samples } | null
  const [binError, setBinError] = useState(null);
  const [logError, setLogError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleBin = (f) => {
    setBinError(null);
    if (!f.name.toLowerCase().endsWith('.bin')) {
      setBinError('File must be .bin');
      return;
    }
    if (f.size > MAX_BIN_BYTES) {
      setBinError(`Bin exceeds 16 MB limit (${(f.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    if (f.size !== 2097152) {
      setBinError(`Bin must be exactly 2 MB (2097152 bytes) MSV80. Yours: ${f.size} bytes.`);
      return;
    }
    setCurrentBin(f);
  };

  const handleLog = async (f) => {
    setLogError(null);
    setDetectedMode(null);
    const nm = f.name.toLowerCase();
    if (!nm.endsWith('.csv') && !nm.endsWith('.log') && !nm.endsWith('.txt')) {
      setLogError('Must be .csv, .log, or .txt');
      return;
    }
    if (f.size > MAX_LOG_BYTES) {
      setLogError(`Datalog exceeds 8 MB limit (${(f.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    setDatalog(f);
    // Auto-detect idle vs WOT and flip the mode toggle so the user doesn't
    // have to remember to click "Idle Stability" before dropping an idle log.
    const det = await detectLogMode(f);
    if (det) {
      setDetectedMode(det);
      if (det.mode && det.mode !== mode) setMode(det.mode);
    }
  };

  // Consume a live datalog handed off from the Live Data tab ("Send to AI Iterate").
  useEffect(() => {
    const pending = window.__pendingLiveLog;
    if (pending) { window.__pendingLiveLog = null; handleLog(pending); }
    const onLiveLog = (e) => { if (e.detail?.file) handleLog(e.detail.file); };
    window.addEventListener('live-log-to-iterate', onLiveLog);
    return () => window.removeEventListener('live-log-to-iterate', onLiveLog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = () => {
    if (!currentBin || !datalog) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    setUploadPct(0);

    const token = localStorage.getItem('auth_token');
    const fd = new FormData();
    fd.append('current_bin', currentBin);
    fd.append('datalog', datalog);
    fd.append('fuel', fuel);
    fd.append('mode', mode);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/tunes/ai-iterate`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) setUploadPct((evt.loaded / evt.total) * 85);
    };
    xhr.upload.onload = () => setUploadPct(90);
    xhr.onload = () => {
      setSubmitting(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.ok === false) {
            setError(data.error || 'Analysis returned no changes');
            setResult(data);
          } else {
            setUploadPct(100);
            setResult(data);
          }
        } catch (e) {
          setError('Server returned an invalid response');
        }
      } else {
        let detail = `Server error (HTTP ${xhr.status})`;
        try {
          const err = JSON.parse(xhr.responseText);
          if (err.detail) detail = err.detail;
        } catch (_) { /* keep default */ }
        setError(detail);
      }
    };
    xhr.onerror = () => {
      setSubmitting(false);
      setError('Network error — check connection');
    };
    xhr.send(fd);
  };

  const reset = () => {
    setCurrentBin(null);
    setDatalog(null);
    setBinError(null);
    setLogError(null);
    setError(null);
    setResult(null);
    setUploadPct(0);
    setSubmitting(false);
    setDetectedMode(null);
  };

  const canRun = currentBin && datalog && !submitting;
  const hasResult = result && result.ok !== false;

  return (
    <div className="max-w-3xl mx-auto" data-testid="ai-tune-iterator">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-400">AI Iterative Tuner</span>
        </div>
        <h2 className="text-2xl font-bold text-white font-['Outfit']">Feed me your tune + a datalog → get the next revision</h2>
        <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
          Drop the bin you&apos;re currently running plus <b className="text-white">either a WOT pull or a warm-idle log</b>.
          I auto-detect which one you gave me — WOT logs get AFR+timing edits at the top of the RPM band,
          idle logs get AFR-target flattening on rows 0-3 to kill warm-idle hunting. New .bin comes out ready to flash.
        </p>
      </div>

      {/* Liability */}
      <div className="mb-5 bg-gradient-to-r from-amber-950/40 to-red-950/30 border border-amber-500/40 rounded-sm px-4 py-2.5 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] leading-relaxed">
          <p className="text-amber-200 font-bold uppercase tracking-wider text-[10px]">Experimental tuning — you assume all risk.</p>
          <p className="text-amber-100/80">Stock backup always saved. ECU restoration available.</p>
        </div>
      </div>

      {/* Upload zones */}
      {!hasResult && (
        <div className="space-y-3">
          <DropZone
            label="Current tune (.bin)"
            sublabel="The 2 MB MSV80 bin currently flashed"
            accept=".bin"
            maxLabel="16 MB max"
            icon={Cpu}
            file={currentBin}
            onFile={handleBin}
            error={binError}
            testId="ai-iter-bin"
            accent="[#FF5E1A]"
          />
          {binError && (
            <p className="text-[11px] text-red-400 flex items-center gap-1.5" data-testid="ai-iter-bin-error">
              <AlertTriangle className="w-3 h-3" />{binError}
            </p>
          )}

          <DropZone
            label="Datalog from that tune"
            sublabel="Idle log OR WOT pull — auto-detected on drop"
            accept=".csv,.log,.txt"
            maxLabel="8 MB max"
            icon={Activity}
            file={datalog}
            onFile={handleLog}
            error={logError}
            testId="ai-iter-log"
            accent="cyan-500"
          />
          {logError && (
            <p className="text-[11px] text-red-400 flex items-center gap-1.5" data-testid="ai-iter-log-error">
              <AlertTriangle className="w-3 h-3" />{logError}
            </p>
          )}
          {detectedMode && !logError && (
            <div
              className={`text-[11px] flex items-start gap-1.5 rounded-sm px-2.5 py-1.5 border ${
                detectedMode.mode === 'idle'
                  ? 'text-cyan-200 bg-cyan-950/30 border-cyan-500/40'
                  : detectedMode.mode === 'wot'
                    ? 'text-emerald-200 bg-emerald-950/30 border-emerald-500/40'
                    : 'text-zinc-300 bg-zinc-900/40 border-zinc-700'
              }`}
              data-testid="ai-iter-log-detected"
            >
              <Wand2 className="w-3 h-3 mt-px flex-shrink-0" />
              <span>
                {detectedMode.mode === 'idle' && <><b>Idle log detected</b> — mode auto-switched to Idle Stability. </>}
                {detectedMode.mode === 'wot' && <><b>WOT pull detected</b> — mode auto-switched to WOT Power. </>}
                {!detectedMode.mode && <><b>Mixed log</b> — pick a mode below. </>}
                <span className="text-zinc-400">{detectedMode.reason}</span>
              </span>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Fuel:</span>
            <div className="flex gap-1.5">
              {['E85', 'gasoline'].map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFuel(f)}
                  className={`px-3 py-1 text-[11px] font-['JetBrains_Mono'] uppercase rounded-sm border transition-all ${
                    fuel === f
                      ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300'
                      : 'border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'
                  }`}
                  data-testid={`ai-iter-fuel-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold ml-3">Mode:</span>
            <div className="flex gap-1.5">
              {[
                { id: 'wot', label: 'WOT Power', hint: 'AFR/timing at 5500-7000 RPM' },
                { id: 'idle', label: 'Idle Stability', hint: 'Fix warm idle hunting' },
              ].map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  title={m.hint}
                  className={`px-3 py-1 text-[11px] font-['JetBrains_Mono'] uppercase rounded-sm border transition-all ${
                    mode === m.id
                      ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                      : 'border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'
                  }`}
                  data-testid={`ai-iter-mode-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          {mode === 'idle' && (
            <div className="bg-cyan-950/20 border border-cyan-500/30 rounded-sm px-3 py-2 text-[11px] text-cyan-200/90 leading-relaxed">
              <span className="font-bold text-cyan-300">Idle mode:</span> upload a warm-idle datalog (engine at 195°F+, in park,
              no pedal). The AI reads idle AFR + RPM stdev and flattens AFR target on rows 0-3 to stop hunting caused by the
              warmup ↔ closed-loop transition on E85 tunes. No WOT edits.
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-sm p-3 text-xs text-red-300 flex items-start gap-2" data-testid="ai-iter-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-px" />
              <div className="min-w-0">
                <div className="font-bold mb-1">Analysis failed</div>
                <div>{error}</div>
                {result?.buckets_found && Object.keys(result.buckets_found).length > 0 && (
                  <div className="text-[10px] text-red-200/70 mt-1.5">
                    Buckets found: {Object.keys(result.buckets_found).join(', ')} · rows parsed: {result.rows_parsed}
                  </div>
                )}
              </div>
            </div>
          )}

          {submitting && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                <span className="text-xs text-zinc-400">
                  {uploadPct < 90 ? 'Uploading…' : 'Analyzing datalog + generating next tune…'}
                </span>
              </div>
              <div className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-sm h-2 overflow-hidden" data-testid="ai-iter-progress">
                <div
                  className="h-full bg-gradient-to-r from-[#FF5E1A] to-emerald-400 transition-[width] duration-200"
                  style={{ width: `${Math.max(0, Math.min(100, uploadPct))}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              onClick={run}
              disabled={!canRun}
              className={`px-6 py-3 rounded-sm font-bold text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-all ${
                canRun
                  ? 'bg-gradient-to-r from-[#FF5E1A] to-emerald-500 hover:from-[#FF7A42] hover:to-emerald-400 text-white shadow-[0_0_28px_rgba(255,94,26,0.4)]'
                  : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              }`}
              data-testid="ai-iter-run"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Generate Next Tune
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Result panel */}
      {hasResult && (
        <div className="space-y-4" data-testid="ai-iter-result">
          <div className="bg-gradient-to-br from-emerald-950/30 to-[#0a0a0a] border border-emerald-500/40 rounded-sm p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-400">Next tune ready</span>
                </div>
                <h3 className="text-lg font-bold text-white font-['Outfit']">{result.version}</h3>
                <p className="text-[11px] text-zinc-500 font-['JetBrains_Mono'] truncate mt-0.5">{result.filename}</p>
              </div>
              <a
                href={`${API_URL}${result.download_url}`}
                download={result.filename}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-2 shadow-[0_0_20px_rgba(16,185,129,0.4)] flex-shrink-0"
                data-testid="ai-iter-download"
              >
                <Download className="w-3.5 h-3.5" />
                Download .bin
              </a>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Fuel cells</div>
                <div className="text-xl text-emerald-300 font-bold font-['JetBrains_Mono']">
                  {result.enrichment_cells_scaled}
                </div>
                <div className="text-[10px] text-zinc-600">
                  +{Math.round((result.scaling_strategy?.enrichment_scale - 1) * 100)}% enrichment
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Timing cells</div>
                <div className="text-xl text-amber-300 font-bold font-['JetBrains_Mono']">
                  {result.timing_cells_pulled}
                </div>
                <div className="text-[10px] text-zinc-600">
                  {result.scaling_strategy?.timing_actual_delta_deg}° pulled
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Bytes changed</div>
                <div className="text-xl text-white font-bold font-['JetBrains_Mono']">
                  {result.total_bytes_changed_vs_base}
                </div>
                <div className="text-[10px] text-zinc-600">
                  sha256 {result.sha256?.slice(0, 8)}…
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">AI reasoning</span>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed" data-testid="ai-iter-reasoning">
              {result.ai_reasoning}
            </p>
          </div>

          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-3.5 h-3.5 text-[#FF5E1A]" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">
                Datalog analysis · {result.rows_parsed} rows parsed
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {Object.entries(result.buckets_analyzed || {}).map(([band, b]) => (
                <div key={band} className="bg-[#050505] border border-[#1f1f1f] rounded-sm px-2 py-1.5 text-[11px] font-['JetBrains_Mono']">
                  <span className="text-zinc-500">{band} RPM</span>
                  <span className="text-white ml-2">AFR {b.afr_mean.toFixed(2)}</span>
                  <span className="text-zinc-500 ml-2">n={b.samples}</span>
                  <span className={`ml-2 ${b.knock_max > 1.5 ? 'text-red-400' : b.knock_max > 1.0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    knock {b.knock_max.toFixed(2)}V
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-cyan-950/20 border border-cyan-500/30 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-400">Flash it, log again, come back</span>
            </div>
            <ul className="text-xs text-zinc-400 space-y-1">
              {[
                'Flash this bin using your normal tool (MSV80 QuickFlash, MPPS, etc.). Full flash — this is a normal cal-block edit.',
                'Log a fresh 3rd-gear WOT pull on this new tune, then come back and drop the new bin + new datalog here for V+2.',
                'Iteration halts automatically when AFR is within 0.3 of target and knock < 1.2V (AI returns "no changes needed").',
              ].map((line) => (
                <li key={line.slice(0, 15)} className="flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={reset}
            className="text-xs text-zinc-500 hover:text-white transition-colors"
            data-testid="ai-iter-reset"
          >
            ← Iterate on another tune
          </button>
        </div>
      )}

      {/* Error state — show reset when we returned error but not hasResult */}
      {result && result.ok === false && (
        <button
          onClick={reset}
          className="mt-4 text-xs text-zinc-500 hover:text-white transition-colors"
          data-testid="ai-iter-reset-err"
        >
          ← Try again
        </button>
      )}
    </div>
  );
}
